import { Link, useRouter } from '@tanstack/react-router';
import { ServerCrash, ShieldAlert } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { Button } from '@/components/health-ui';
import {
  apiErrorReferenceId,
  apiErrorStatus,
  isChunkLoadError,
} from '@/lib/api-error';
import { ErrorState } from '@/components/error-state';
import { NotFoundState } from '@/components/not-found-state';

type ContentRouteErrorComponentProps = {
  error: unknown;
  reset: () => void;
};

// A transient route-chunk load failure resolves on a re-attempt, so retry a few
// times behind a quiet loading state instead of flashing the alarming 500
// screen. Bounded + time-windowed (module scope, survives the error boundary
// remounting on each failed attempt) so a genuinely missing chunk still falls
// through to the real error UI rather than looping forever.
const CHUNK_RETRY_LIMIT = 3;
const CHUNK_RETRY_WINDOW_MS = 10_000;
const CHUNK_RETRY_DELAY_MS = 200;

let chunkRetries = { count: 0, windowStart: 0 };

function consumeChunkRetryBudget(now: number): boolean {
  if (now - chunkRetries.windowStart > CHUNK_RETRY_WINDOW_MS) {
    chunkRetries = { count: 0, windowStart: now };
  }
  if (chunkRetries.count >= CHUNK_RETRY_LIMIT) {
    return false;
  }
  chunkRetries.count += 1;
  return true;
}

export function ContentRouteErrorComponent({
  error,
  reset,
}: Readonly<ContentRouteErrorComponentProps>): React.JSX.Element {
  const router = useRouter();
  const status = apiErrorStatus(error);

  const handleRetry = async () => {
    // Invalidate all queries/loaders and reset the boundary.
    // Order matters: invalidate first, then reset to avoid flashing stale state.
    await router.invalidate();
    reset();
  };

  // Auto-recover from a transient route-chunk load failure: reset the boundary
  // (which re-attempts the lazy import) behind a loading state, so users never
  // see the 500 screen for a hiccup that fixes itself on the next attempt.
  const chunkError = isChunkLoadError(error);
  const [chunkRetriesExhausted, setChunkRetriesExhausted] = useState(false);
  const resetRef = useRef(reset);
  resetRef.current = reset;

  useEffect(() => {
    if (!chunkError) {
      return;
    }
    if (!consumeChunkRetryBudget(Date.now())) {
      setChunkRetriesExhausted(true);
      return;
    }
    const timer = window.setTimeout(
      () => resetRef.current(),
      CHUNK_RETRY_DELAY_MS,
    );
    return () => window.clearTimeout(timer);
  }, [chunkError]);

  // While a chunk retry is pending, show a calm loading state — not the 500.
  if (chunkError && !chunkRetriesExhausted) {
    return (
      <div
        className="flex min-h-[60vh] items-center justify-center"
        role="status"
        aria-live="polite"
        data-testid="route-chunk-retrying"
      >
        <div className="flex flex-col items-center gap-3">
          <div
            className="size-8 animate-spin rounded-full border-2 border-muted border-t-primary"
            aria-hidden
          />
          <p className="text-sm text-muted-foreground">Loading…</p>
        </div>
      </div>
    );
  }

  const notFoundActions = [
    <Button key="patients" asChild>
      <Link to="/patients">Back to Patients</Link>
    </Button>,
    <Button key="dashboard" variant="outline" asChild>
      <Link to="/dashboard">Go to Dashboard</Link>
    </Button>,
  ];

  const errorActions = [
    <Button key="retry" onClick={handleRetry}>
      Try again
    </Button>,
    <Button key="dashboard" variant="outline" asChild>
      <Link to="/dashboard">Go to Dashboard</Link>
    </Button>,
  ];

  // 404: render the not-found screen
  if (status === 404) {
    return <NotFoundState actions={notFoundActions} />;
  }

  if (status === 403) {
    return (
      <ErrorState
        tone="danger"
        icon={ShieldAlert}
        code="403"
        headline="Access denied"
        description="Your account does not have access to this Medintelekt area."
        actions={errorActions}
        referenceId={apiErrorReferenceId(error)}
        compact={false}
      />
    );
  }

  // Everything else (500, unknown, etc.): render the generic error screen
  return (
    <ErrorState
      tone="danger"
      icon={ServerCrash}
      code="500"
      headline="Something went wrong on our end"
      description="We hit an unexpected error while loading this page. Your data is safe. Please try again in a moment, or contact support if the problem continues."
      actions={errorActions}
      referenceId={apiErrorReferenceId(error)}
      compact={false}
    />
  );
}
